import "./SessionHistoryComponent.scss";
import { APIConfig } from "@/constants";
import { useCallback, useEffect, useState } from "react";
import commonService from "@/services/common.service.ts";
import { Sheet, SheetContent, SheetHeader } from "@/components/ui/sheet.tsx";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button.tsx";
import { AppType } from "@/models/project.model.ts";
import { Clock, Check } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import LoaderComponent from "@/components/common-components/loader/LoaderComponent.tsx";
import CommonService from "@/services/common.service.ts";

interface SessionHistoryComponentProps {
    projectId: string;
    isHistoryDrawerOpen: boolean;
    onClose: () => void;
    appType: AppType;
}

const SessionHistoryComponent = (props: SessionHistoryComponentProps) => {
    const { projectId, isHistoryDrawerOpen, onClose } = props;
    const isMobile = useIsMobile();

    const [sessions, setSessions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [currentSession, setCurrentSession] = useState<any>(null);
    const [revertingSessionId, setRevertingSessionId] = useState<string | null>(null);

    const revertSession = useCallback((session: any) => {
        setRevertingSessionId(session.id);
        commonService._communications.setPreviewLoadingStateSubject.next(true);

        commonService._project_service.revertToASession(projectId, {
            session_id: session.id,
        })
            .then(() => {
                setRevertingSessionId(null);
                setCurrentSession(session);
                commonService._communications.fetchProjectCode.next(true);
                commonService._alert.showToast("Session restored successfully", "success");
                onClose();
            })
            .catch(() => {
                setRevertingSessionId(null);
                commonService._alert.showToast("Failed to restore session", "error");
            });
    }, [projectId, onClose]);

    const fetchSessions = useCallback(() => {
        setIsLoading(true);
        setError(false);
        setSessions([])
        commonService._api.get(APIConfig.PROJECT_SESSIONS.URL(projectId))
            .then((response) => {
                const allCompleted = response?.data?.completed_sessions || [];
                const filteredCompleted = allCompleted?.filter((s: any) => s.chat_mode === false);

                const active = response?.data?.active_session;
                const activeSession = active && active.chat_mode === false ? active : null;

                setSessions(filteredCompleted);
                setCurrentSession(activeSession);
                setIsLoading(false);
            })
            .catch(() => {
                CommonService.handleMixpanelTrack("session_history_fetch_error", {
                    project_id: projectId,
                    timestamp: new Date().toISOString()
                });
                setError(true);
                setIsLoading(false);
            });
    }, [projectId]);

    useEffect(() => {
        if (isHistoryDrawerOpen) {
            fetchSessions();
        }
    }, [isHistoryDrawerOpen, fetchSessions]);

    const renderSessionCard = (session: any) => {
        const isActiveSession = currentSession?.id === session.id;
        const isReverting = revertingSessionId === session.id;

        return (
            <div
                key={session.id}
                className={`session-card ${isActiveSession ? 'active-session' : ''}`}
            >
                <div className="session-card-header">
                    <div className="session-icon">
                        <Clock className="w-4 h-4" />
                    </div>
                    <div className="session-info">
                        <div className="session-title">
                            {session?.description || "Untitled Session"}
                        </div>
                        <div className="session-meta">
                            <span className="session-date">
                                {commonService.transformTimeStamp(session?.created_at)}
                            </span>
                            {isActiveSession && (
                                <span className="active-badge">
                                    <Check className="w-2.5 h-2.5" />
                                    Active
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="session-card-actions">
                    <Button
                        type="button"
                        size="xs"
                        variant={isActiveSession ? "outline" : "default"}
                        loading={isReverting}
                        loadingText="Restoring..."
                        onClick={() => revertSession(session)}
                        disabled={isActiveSession || isReverting}
                        mixpanelEvent={{
                            name: "session_restore_clicked",
                            properties: {
                                project_id: projectId,
                                session_id: session.id,
                                is_active: isActiveSession
                            }
                        }}
                    >
                        {isActiveSession ? "Active" : "Restore"}
                    </Button>
                </div>
            </div>
        );
    };

    const renderContent = () => (
        <>
            <div className="history-header">
                <SheetHeader>
                    <h2 className="history-title">Session History</h2>
                    <p className="history-description">
                        View and restore previous versions of your project
                    </p>
                </SheetHeader>
            </div>

            <div className="history-body">
                {isLoading ? (
                    <div className="history-loading">
                        <LoaderComponent />
                    </div>
                ) : error ? (
                    <div className="history-error">
                        <p className="error-message">Failed to load sessions</p>
                        <Button
                            variant="outline"
                            size="xs"
                            onClick={fetchSessions}
                            mixpanelEvent={{
                                name: "session_history_retry_clicked",
                                properties: { project_id: projectId }
                            }}
                        >
                            Retry
                        </Button>
                    </div>
                ) : sessions.length === 0 ? (
                    <div className="history-empty">
                        <Clock className="empty-icon" />
                        <p className="empty-message">No session history yet</p>
                        <p className="empty-hint">
                            Sessions will appear here as you make changes to your project
                        </p>
                    </div>
                ) : (
                    <div className="sessions-list">
                        {sessions.map((session) => renderSessionCard(session))}
                    </div>
                )}
            </div>
        </>
    );

    if (isMobile) {
        return (
            <Drawer open={isHistoryDrawerOpen} onOpenChange={onClose}>
                <DrawerContent className="h-[90vh] p-0">
                    <div className="session-history-mobile">
                        {renderContent()}
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Sheet open={isHistoryDrawerOpen} onOpenChange={onClose}>
            <SheetContent side="left" className="session-history-desktop">
                {renderContent()}
            </SheetContent>
        </Sheet>
    );
};

export default SessionHistoryComponent;
